/*
  Tagalys Autosuggest (net-new)
  =============================
  Binds Tagalys' ShopifySearchSuggestions widget to the theme's search text
  inputs so typing surfaces Tagalys-powered suggestions, while leaving Horizon's
  native predictive search untouched when Tagalys is disabled.

  Loaded ONLY when settings.tagalys_enabled AND settings.tagalys_search_enabled
  (gated <script type="module"> in snippets/search-modal.liquid). When the gate
  is off this file is never requested, so assets/predictive-search.js continues
  to drive the modal as the native fallback.

  Inputs bound (see snippets/search-modal.liquid, blocks/_search-input.liquid):
    - '#search-modal .search-input'  — the shared header + mobile-drawer modal
      text input (#cmdk-input). Both the header and mobile "search" buttons open
      #search-modal, so this single input covers desktop popover and mobile
      overlay entry points.
    - '.search-page-input'           — the on-page /search results input
      (present only on the search template).

  Contract preserved:
    - The #search-modal <dialog> shell and its <form method="get"
      action="{{ routes.search_url }}"> stay intact, so Enter / "view all"
      submits q to /search (the U7 results page).
    - Tagalys binds to the existing inputs; it does not replace the form.

  Dependencies:
    - snippets/tagalys-config.liquid provides the global onTagalysReady(cb)
      lifecycle and loads the SDK.
    - window.Theme.routes.search_url from snippets/scripts.liquid (falls back to
      '/search').
*/

(() => {
  "use strict";

  const MINIMUM_CHARACTERS_TO_SHOW_SUGGESTIONS = 2;

  // Resolve the canonical Shopify /search route exposed by scripts.liquid.
  function getSearchResultsURL() {
    const themeRoutes = window.Theme && window.Theme.routes;
    if (themeRoutes && themeRoutes.search_url) {
      return themeRoutes.search_url;
    }
    return "/search";
  }

  // Track which selectors have already been handed to the SDK so re-inits
  // are idempotent for the page input. The modal input is always re-bound on
  // dialog open (deleted from this set before each open).
  const boundSelectors = new Set();

  function buildConfig() {
    const config = {
      // Enter / submit / "view all" must land on the native /search results
      // page so the U7 results experience and the GET ?q= contract hold.
      searchResultsURL: getSearchResultsURL(),
      minimumCharactersToShowSuggestions:
        MINIMUM_CHARACTERS_TO_SHOW_SUGGESTIONS,
    };

    // Zero-query state: surface popular/recent searches when the SDK exposes
    // the helper. The shape is read defensively so a missing/!renamed helper
    // degrades to an empty suggestions panel rather than throwing.
    // TODO(tagalys-live): confirm the exact accessor and the popular/recent
    // config key names against the live preview store
    // (linen-house-au.myshopify.com, preview_theme_id=154703495341). The
    // documented surface is getRecentAndPopularSearches(); the returned shape
    // and the config field it feeds (e.g. `zeroQuery`/`popularSearches`) are
    // version-specific.
    const tagalys = window.Tagalys;
    const suggestions =
      tagalys &&
      tagalys.UIWidgets &&
      (tagalys.UIWidgets.SearchSuggestions ||
        tagalys.UIWidgets.ShopifySearchSuggestions);
    if (
      suggestions &&
      typeof suggestions.getRecentAndPopularSearches === "function"
    ) {
      try {
        config.recentAndPopularSearches =
          suggestions.getRecentAndPopularSearches();
      } catch (error) {
        // Non-fatal — fall back to the SDK's default zero-query behaviour.
        console.warn(
          "[tagalys-autosuggest] zero-query state unavailable",
          error,
        );
      }
    }

    return config;
  }

  // Resolve the Tagalys autosuggest component. Prefer the Tagalys-POWERED
  // SearchSuggestions (queries Tagalys); ShopifySearchSuggestions is the
  // Shopify-powered fallback (queries Shopify) and is only a last resort.
  function getSuggestionsWidget() {
    const widgets = window.Tagalys && window.Tagalys.UIWidgets;
    if (!widgets) return null;
    return (
      widgets.SearchSuggestions || widgets.ShopifySearchSuggestions || null
    );
  }

  function initSuggestions() {
    const widget = getSuggestionsWidget();
    if (!widget || typeof widget.init !== "function") {
      // SDK not ready / component absent — onTagalysReady should have guaranteed
      // readiness, so this only guards against a renamed/missing component.
      return;
    }

    const selectorsToBind = presentSelectors().filter(
      (selector) => !boundSelectors.has(selector),
    );
    if (selectorsToBind.length === 0) return;

    // The SDK accepts an ARRAY of selectors so the desktop popover and the
    // mobile overlay are bound in a single init call.
    widget.init(selectorsToBind, buildConfig());
    selectorsToBind.forEach((selector) => boundSelectors.add(selector));
  }

  const MODAL_SELECTOR = "#search-modal .search-input";
  const PAGE_SELECTOR = ".search-page-input";

  // The search modal input lives inside a <dialog> that is closed on page load.
  // When onTagalysReady fires (DOMContentLoaded), the dialog has display:none so
  // the input reports zero dimensions — the SDK bails on positioning and the
  // suggestions container ends up static at the bottom of <body>, never receiving
  // input events. Fix: skip the modal input at page load and re-init it each time
  // the dialog opens, when the input is actually visible and has real dimensions.
  function bindModalInput() {
    const widget = getSuggestionsWidget();
    if (!widget || typeof widget.init !== "function") return;

    // Always re-bind on open so the SDK picks up the correct input position.
    boundSelectors.delete(MODAL_SELECTOR);

    const selectorsToBind = [MODAL_SELECTOR].filter((s) =>
      document.querySelector(s),
    );
    if (selectorsToBind.length === 0) return;

    widget.init(selectorsToBind, buildConfig());
    selectorsToBind.forEach((s) => boundSelectors.add(s));
  }

  // Tagalys appends .tagalys-search-suggestions to <body>, which renders below
  // the <dialog> top-layer. Move it inside the form so it inherits the dialog
  // stacking context and sits below the search header — same slot as the native
  // predictive-search-form__content-wrapper.
  function relocateSuggestionsIntoDialog() {
    const form = document.querySelector("#search-modal .predictive-search-form");
    if (!form) return;

    new MutationObserver((mutations, obs) => {
      const suggestions = document.querySelector(
        ".tagalys-search-suggestions:not(#search-modal .tagalys-search-suggestions)"
      );
      if (!suggestions) return;
      obs.disconnect();
      // Strip inline position Tagalys set (viewport-relative coords, wrong after reparent).
      suggestions.style.position = "";
      suggestions.style.top = "";
      suggestions.style.left = "";
      suggestions.style.width = "";
      suggestions.style.zIndex = "";
      form.appendChild(suggestions);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function observeModalOpen() {
    const dialog = document.querySelector("#search-modal dialog");
    if (!dialog) return;

    new MutationObserver(() => {
      if (dialog.hasAttribute("open")) {
        relocateSuggestionsIntoDialog();
        bindModalInput();
        wireEmptyState();
        // Un-hide the container FIRST so the widgets init with real dimensions
        // (Tagalys bails on rendering into a display:none element), then init.
        updateEmptyState();
        initEmptyWidgets();
        // If the input still holds a query from last time, Tagalys can reopen in
        // a stale "type 2 characters" state — nudge it to re-query so it shows
        // results instead (tidies the messy reopen).
        const reopenInput = document.querySelector(MODAL_SELECTOR);
        if (
          reopenInput &&
          (reopenInput.value || "").trim().length >=
            MINIMUM_CHARACTERS_TO_SHOW_SUGGESTIONS
        ) {
          reopenInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    }).observe(dialog, { attributes: true, attributeFilter: ["open"] });
  }

  // The on-page /search input is always visible — bind it immediately on ready.
  function bindPageInput() {
    const widget = getSuggestionsWidget();
    if (!widget || typeof widget.init !== "function") return;

    const selectorsToBind = [PAGE_SELECTOR].filter(
      (s) => !boundSelectors.has(s) && document.querySelector(s),
    );
    if (selectorsToBind.length === 0) return;

    widget.init(selectorsToBind, buildConfig());
    selectorsToBind.forEach((s) => boundSelectors.add(s));
  }

  // ---- Brand injection (Carina feedback item 14) ---------------------------
  // Autosuggest tiles ship as image + name + price only. Mirror the native card
  // by adding the vendor above the title. Vendor isn't in the tile markup, so we
  // reuse the per-handle ?view=tagalys-swatches endpoint (it returns `vendor`),
  // cached by handle. The panel re-renders on every keystroke, so we re-scan on
  // a debounced document observer and re-apply from cache synchronously.
  const ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";
  const brandCache = Object.create(null); // handle -> vendor ("" = none)
  const brandInflight = Object.create(null);
  // Tiles enriched with a brand line: the live typed-suggestions panel AND the
  // empty-state Recently Viewed / Recommended widgets.
  const BRAND_TILE_SELECTOR =
    ".tagalys-search-suggestions .product-link, .tagalys-autosuggest-empty .product-link";

  function handleFromLink(link) {
    const href = link.getAttribute("href") || "";
    const m = href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function applyBrand(link, vendor) {
    if (!vendor) return;
    const details = link.querySelector(".product-details");
    if (!details || details.querySelector(".tgls-card-brand")) return;
    const brand = document.createElement("span");
    brand.className = "tgls-card-brand exclude-tagalys-css-reset";
    brand.textContent = vendor;
    const name = details.querySelector(".product-name");
    details.insertBefore(brand, name || details.firstChild);
  }

  function fetchBrand(handle) {
    if (brandInflight[handle]) return;
    brandInflight[handle] = true;
    fetch(ROOT + "products/" + encodeURIComponent(handle) + "?view=tagalys-swatches")
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((text) => {
        let vendor = "";
        try {
          vendor = JSON.parse(text.trim()).vendor || "";
        } catch (e) {
          vendor = "";
        }
        brandCache[handle] = vendor;
        delete brandInflight[handle];
        document.querySelectorAll(BRAND_TILE_SELECTOR).forEach((l) => {
          if (handleFromLink(l) === handle) applyBrand(l, vendor);
        });
      })
      .catch(() => {
        brandCache[handle] = "";
        delete brandInflight[handle];
      });
  }

  // Recommendation-widget tiles ship images as lazy data-src with no src (the
  // SDK's own swap doesn't fire in this context), so they render blank. Swap
  // data-src -> src for any suggestion/widget image that's missing its src.
  function swapImages() {
    document
      .querySelectorAll(
        ".tagalys-search-suggestions img[data-src], .tagalys-autosuggest-empty img[data-src]",
      )
      .forEach((img) => {
        const src = img.getAttribute("data-src");
        if (src && img.getAttribute("src") !== src) img.setAttribute("src", src);
      });
  }

  function scanBrands() {
    swapImages();
    const links = document.querySelectorAll(BRAND_TILE_SELECTOR);
    links.forEach((link) => {
      if (link.querySelector(".tgls-card-brand")) return;
      const handle = handleFromLink(link);
      if (!handle) return;
      const cached = brandCache[handle];
      if (cached !== undefined) applyBrand(link, cached);
      else fetchBrand(handle);
    });
  }

  let brandRaf = null;
  function observeBrands() {
    new MutationObserver(() => {
      if (brandRaf) return;
      brandRaf = requestAnimationFrame(() => {
        brandRaf = null;
        scanBrands();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ---- Empty-state widgets (Carina feedback items 10–11) -------------------
  // Recently Viewed + Recommended for you, rendered by Tagalys Recommendations
  // widgets into the [data-tagalys-autosuggest-widget] containers and shown only
  // while the modal input is empty (Tagalys's own suggestions take over on type).
  function initEmptyWidgets() {
    const recs =
      window.Tagalys &&
      window.Tagalys.UIWidgets &&
      window.Tagalys.UIWidgets.Recommendations;
    if (!recs || typeof recs.init !== "function") return;

    // Tagalys keys its render state by recommendationId, so two widgets sharing
    // an id collide and neither mounts. Init each distinct id once; hide any row
    // that's a duplicate (or has no id) so a shared id never wipes both rows.
    const seenIds = Object.create(null);
    document
      .querySelectorAll("[data-tagalys-autosuggest-widget]")
      .forEach((el, i) => {
        if (el.getAttribute("data-tgls-init")) return;
        const id = el.getAttribute("data-widget-id");
        const section = el.closest(".tagalys-autosuggest-widget");
        if (!id || seenIds[id]) {
          if (section) section.hidden = true;
          return;
        }
        seenIds[id] = true;
        el.setAttribute("data-tgls-init", "1");
        // Unique selector so init never matches >1 element.
        const uid =
          "tgls-asw-" + i + "-" + Math.random().toString(36).slice(2, 8);
        el.setAttribute("data-tgls-uid", uid);
        try {
          recs.init('[data-tgls-uid="' + uid + '"]', { recommendationId: id });
        } catch (error) {
          console.warn("[tagalys-autosuggest] widget init failed", id, error);
          el.removeAttribute("data-tgls-init");
          el.removeAttribute("data-tgls-uid");
        }
      });

    // The widget renders asynchronously — nudge the image swap a few times so
    // lazy data-src images get their src once tiles mount.
    [150, 500, 1200].forEach((t) => setTimeout(swapImages, t));
  }

  // Show the empty-state widgets when the input is below the suggestion
  // threshold; hand the panel back to Tagalys once the shopper types. The
  // container carries [data-tagalys-autosuggest-empty] and, while visible, a CSS
  // sibling rule hides Tagalys's own zero-query hint so the two don't stack.
  // Only takes over when the container actually has widgets (IDs configured).
  function updateEmptyState() {
    const empty = document.querySelector("[data-tagalys-autosuggest-empty]");
    const input = document.querySelector(MODAL_SELECTOR);
    if (!empty || !input) return;
    const hasWidgets = !!empty.querySelector("[data-tagalys-autosuggest-widget]");
    const isEmpty =
      (input.value || "").trim().length <
      MINIMUM_CHARACTERS_TO_SHOW_SUGGESTIONS;
    empty.hidden = !(isEmpty && hasWidgets);
    if (!empty.hidden) scanBrands();
  }

  function wireEmptyState() {
    const input = document.querySelector(MODAL_SELECTOR);
    if (!input || input.getAttribute("data-tgls-empty-wired")) return;
    input.setAttribute("data-tgls-empty-wired", "1");
    input.addEventListener("input", updateEmptyState);
  }

  onTagalysReady(() => {
    bindPageInput();
    observeModalOpen();
    observeBrands();
    initEmptyWidgets();
  });
})();
